'use client'

import React, { forwardRef } from 'react'

import { Box, chakra } from '@chakra-ui/react'
import { keyframes } from '@emotion/react'

import {
  ButtonSize,
  ButtonVariant,
  ColorScheme,
  buttonStyles,
} from './Button.styles'

export type { ColorScheme }

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`

const Spinner = () => (
  <Box
    animation={`${spin} 0.7s linear infinite`}
    border='2px solid currentColor'
    borderRadius='full'
    borderTopColor='transparent'
    display='inline-block'
    flexShrink={0}
    height='1em'
    width='1em'
  />
)

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant
  size?: ButtonSize
  /**
   * Border radius of the button. Accepts any CSS value e.g. '0px', '4px',
   * '50%'. Defaults to '0'.
   */
  borderRadius?: string | number
  colorScheme?: ColorScheme
  /** Icon rendered before the label */
  leftIcon?: React.ReactNode
  /** Icon rendered after the label */
  rightIcon?: React.ReactNode
  /** Shows a spinner and disables the button while true */
  isLoading?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = 'primary',
      size = 'md',
      borderRadius,
      colorScheme = 'dark',
      leftIcon,
      rightIcon,
      children,
      disabled,
      isLoading = false,
      ...rest
    },
    ref
  ) {
    const isDisabled = disabled || isLoading

    return (
      <chakra.button
        aria-busy={isLoading}
        aria-disabled={isDisabled}
        disabled={isDisabled}
        ref={ref}
        type='button'
        {...buttonStyles(variant, size, borderRadius, colorScheme)}
        {...rest}
      >
        {isLoading ? (
          <Box alignItems='center' display='inline-flex' gap='6px'>
            <Spinner />
            {children}
          </Box>
        ) : (
          <>
            {leftIcon}
            {children}
            {rightIcon}
          </>
        )}
      </chakra.button>
    )
  }
)
